/**
 * CPK (Corey-Pauling-Koltun) color scheme for chemical elements.
 *
 * Colors are DISABLED by default for backward compatibility.
 * Can be enabled via ENABLE_CPK_COLORS=true env var.
 *
 * Source: ACS 1996 standards (molecular-svg-config.json)
 */

// Runtime flag - can be set via environment variable (defaults to false for backward compatibility)
const CPK_ENABLED = typeof process !== 'undefined'
  ? process.env.ENABLE_CPK_COLORS === 'true'
  : false;

/**
 * CPK color mapping for chemical elements.
 * Maps element symbols to hex color codes.
 */
export const CPK_COLORS: Readonly<Record<string, string>> = {
    H: '#FFFFFF',   // White
    C: '#909090',   // Dark gray
    N: '#8F8FFF',   // Light blue
    O: '#F00000',   // Red
    F: '#90E050',   // Light green
    Cl: '#1FF01F',  // Green
    Br: '#A62929',  // Dark red
    I: '#940094',   // Dark violet
    S: '#FFC832',   // Yellow
    P: '#FF8000',   // Orange
    B: '#FFB5B5',   // Peach
    Li: '#CC80FF',  // Violet
    Na: '#AB5CF2',  // Violet
    K: '#8F40D4',   // Violet
    Mg: '#8AFF00',  // Green
    Ca: '#3DFF00',  // Green
    Fe: '#E06633',  // Orange
} as const;

/**
 * Get the CPK color for a chemical element symbol.
 *
 * @param elementSymbol - 1-2 character element symbol (e.g., "C", "Cl")
 * @returns Hex color string (e.g., "#909090") or "black" if:
 *   - CPK colors disabled (compile-time flag)
 *   - Element not in mapping (fallback)
 *
 * @example
 * ```typescript
 * // With VITE_ENABLE_CPK_COLORS=true
 * getAtomColor('C')  // Returns '#909090' (gray)
 * getAtomColor('N')  // Returns '#8F8FFF' (blue)
 * getAtomColor('O')  // Returns '#F00000' (red)
 * getAtomColor('Zz') // Returns 'black' (unknown element)
 *
 * // With VITE_ENABLE_CPK_COLORS=false (default)
 * getAtomColor('C')  // Returns 'black'
 * getAtomColor('N')  // Returns 'black'
 * ```
 */
export function getAtomColor(elementSymbol: string): string {
    if (!CPK_ENABLED) {
        return 'black';
    }
    return CPK_COLORS[elementSymbol] ?? 'black';
}
